import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Loader2, Activity, Users, ShoppingBag, Star, Server, BarChart3, FileText, BookOpen, ExternalLink, Eye, Edit, Trash2, Menu, Phone, Globe } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CacheClearButton } from "@/components/ui/cache-clear-button";
import { SystemStatus } from "@/components/admin/SystemStatus";
import { ApiMetrics } from "@/components/admin/ApiMetrics";
import { LogsViewer } from "@/components/admin/LogsViewer";
import ErrorBoundary from "@/components/ErrorBoundary";
import type { User } from "@/hooks/use-auth";

/**
 * Prüft ob ein User Admin ist (spiegelt Backend-Logik)
 * Nur rollenbasierte Prüfung — keine Email-Hardcodes
 */
function isAdmin(user: User | null): boolean {
  if (!user) return false;
  if (!user.roles || !Array.isArray(user.roles)) return false;
  return user.roles.some(role => {
    const r = role.toLowerCase();
    return r === 'admin' || r === 'role_admin' || r.includes('admin');
  });
}

function AdminInlineErrorCard({
  title = "Etwas hat nicht geklappt",
  description = "Dieser Bereich konnte nicht geladen werden. Bitte versuche es erneut.",
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry: () => void;
}) {
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-red-50 p-2">
          <Server className="h-5 w-5 text-red-600" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">{title}</div>
          <div className="text-sm text-gray-600 mt-1">{description}</div>
          <div className="mt-4">
            <Button onClick={onRetry} variant="outline">
              Neu laden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Temporär vereinfachter Typ für Restaurant
interface Restaurant {
  id: string;
  name: string;
  slug: string;
  city: string;
  postalCode: string;
  street: string;
  enabled: boolean;
  updatedAt?: string;
  rating?: number;
  delivery?: boolean;
}

// Einfache Dashboard-Statistiken
interface DashboardStats {
  totalRestaurants: number;
  activeRestaurants: number;
  deliveryRestaurants: number;
  averageRating: number;
}

type TabType = "restaurants" | "insights" | "status" | "metrics" | "logs";

// Globale Insights Daten Interface
interface GlobalInsightsData {
  total_events: number;
  unique_sessions: number;
  total_views: number;
  clicks_by_type: {
    call: number;
    route: number;
    menu: number;
    website: number;
  };
  timeseries: Array<{
    date: string;
    total_events: number;
    unique_sessions: number;
    views: number;
    calls: number;
    routes: number;
    menus: number;
    websites: number;
  }>;
  top_restaurants: {
    by_views: Array<{ id: string; name: string; city: string; views: number }>;
    by_routes: Array<{ id: string; name: string; city: string; clicks: number }>;
    by_calls: Array<{ id: string; name: string; city: string; clicks: number }>;
    by_menus: Array<{ id: string; name: string; city: string; clicks: number }>;
    by_websites: Array<{ id: string; name: string; city: string; clicks: number }>;
  };
  event_breakdown: Array<{ event_name: string; count: number }>;
}

// Globales Insights Panel Komponente
function GlobalInsightsPanel() {
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0],
    };
  });

  const { data: insights, isLoading, error } = useQuery<GlobalInsightsData>({
    queryKey: ['/api/admin/insights/global', dateRange.from, dateRange.to],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/admin/insights/global?from=${dateRange.from}&to=${dateRange.to}`);
      if (!res.ok) throw new Error('Fehler beim Laden der globalen Insights');
      return res.json();
    },
    staleTime: 2 * 60 * 1000, // 2 Minuten Cache
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Lade globale Insights...</span>
        </div>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500 py-8">
          {error ? 'Fehler beim Laden der Insights' : 'Keine Daten verfügbar'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Date Range */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-darker">Globale Analytics</h2>
            <p className="text-gray-500 text-sm">Gesamtübersicht aller Restaurants</p>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-gray-500">–</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            />
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => {
                  const to = new Date();
                  const from = new Date();
                  from.setDate(from.getDate() - days);
                  setDateRange({
                    from: from.toISOString().split('T')[0],
                    to: to.toISOString().split('T')[0],
                  });
                }}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
              >
                {days}T
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-blue-600 uppercase">Events</p>
            <p className="text-2xl font-bold text-blue-900">{insights.total_events.toLocaleString('de-DE')}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-green-600 uppercase">Sessions</p>
            <p className="text-2xl font-bold text-green-900">{insights.unique_sessions.toLocaleString('de-DE')}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-purple-600 uppercase">Views</p>
            <p className="text-2xl font-bold text-purple-900">{insights.total_views.toLocaleString('de-DE')}</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-orange-600 uppercase">Anrufe</p>
            <p className="text-2xl font-bold text-orange-900">{insights.clicks_by_type.call.toLocaleString('de-DE')}</p>
          </div>
          <div className="bg-teal-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-teal-600 uppercase">Route</p>
            <p className="text-2xl font-bold text-teal-900">{insights.clicks_by_type.route.toLocaleString('de-DE')}</p>
          </div>
          <div className="bg-pink-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-pink-600 uppercase">Menü</p>
            <p className="text-2xl font-bold text-pink-900">{insights.clicks_by_type.menu.toLocaleString('de-DE')}</p>
          </div>
        </div>
      </div>

      {/* Top Restaurants - Menü prominent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top by Menu (PROMINENT) */}
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg shadow-lg p-6 border-2 border-pink-200">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Menu className="h-6 w-6 text-pink-600" />
            <span className="text-pink-900">🌟 Top 10 nach Speisekarten-Klicks</span>
          </h3>
          <div className="space-y-2">
            {insights.top_restaurants.by_menus?.slice(0, 10).map((r, i) => (
              <Link key={r.id} href={`/admin/restaurants/edit/${r.id}?tab=insights`} className="flex justify-between items-center text-sm bg-white/60 rounded px-2 py-1.5 hover:bg-white/80 transition-colors cursor-pointer group">
                <span className="truncate flex-1">
                  <span className="text-pink-400 mr-2 font-bold">{i + 1}.</span>
                  <span className="font-medium group-hover:text-pink-600">{r.name}</span>
                  <span className="text-gray-500 text-xs ml-1">({r.city})</span>
                </span>
                <span className="font-bold text-pink-700">{r.clicks}</span>
              </Link>
            ))}
            {(!insights.top_restaurants.by_menus || insights.top_restaurants.by_menus.length === 0) && (
              <p className="text-pink-600 text-sm font-medium">Noch keine Menü-Klicks</p>
            )}
          </div>
        </div>

        {/* Top by Views */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-purple-500" />
            Top 10 nach Aufrufen
          </h3>
          <div className="space-y-2">
            {insights.top_restaurants.by_views?.slice(0, 10).map((r, i) => (
              <Link key={r.id} href={`/admin/restaurants/edit/${r.id}?tab=insights`} className="flex justify-between items-center text-sm hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors cursor-pointer group">
                <span className="truncate flex-1">
                  <span className="text-gray-400 mr-2">{i + 1}.</span>
                  <span className="group-hover:text-purple-600">{r.name}</span>
                </span>
                <span className="font-medium text-purple-600">{r.views}</span>
              </Link>
            ))}
            {(!insights.top_restaurants.by_views || insights.top_restaurants.by_views.length === 0) && (
              <p className="text-gray-400 text-sm">Noch keine Daten</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Restaurants Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Top by Calls */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Phone className="h-5 w-5 text-orange-500" />
            Top 10 nach Anrufen
          </h3>
          <div className="space-y-2">
            {insights.top_restaurants.by_calls?.slice(0, 10).map((r, i) => (
              <Link key={r.id} href={`/admin/restaurants/edit/${r.id}?tab=insights`} className="flex justify-between items-center text-sm hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors cursor-pointer group">
                <span className="truncate flex-1">
                  <span className="text-gray-400 mr-2">{i + 1}.</span>
                  <span className="group-hover:text-orange-600">{r.name}</span>
                </span>
                <span className="font-medium text-orange-600">{r.clicks}</span>
              </Link>
            ))}
            {(!insights.top_restaurants.by_calls || insights.top_restaurants.by_calls.length === 0) && (
              <p className="text-gray-400 text-sm">Noch keine Daten</p>
            )}
          </div>
        </div>

        {/* Top by Route */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-teal-500" />
            Top 10 nach Route
          </h3>
          <div className="space-y-2">
            {insights.top_restaurants.by_routes?.slice(0, 10).map((r, i) => (
              <Link key={r.id} href={`/admin/restaurants/edit/${r.id}?tab=insights`} className="flex justify-between items-center text-sm hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors cursor-pointer group">
                <span className="truncate flex-1">
                  <span className="text-gray-400 mr-2">{i + 1}.</span>
                  <span className="group-hover:text-teal-600">{r.name}</span>
                </span>
                <span className="font-medium text-teal-600">{r.clicks}</span>
              </Link>
            ))}
            {(!insights.top_restaurants.by_routes || insights.top_restaurants.by_routes.length === 0) && (
              <p className="text-gray-400 text-sm">Noch keine Daten</p>
            )}
          </div>
        </div>

        {/* Top by Website */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Top 10 nach Website
          </h3>
          <div className="space-y-2">
            {insights.top_restaurants.by_websites?.slice(0, 10).map((r, i) => (
              <Link key={r.id} href={`/admin/restaurants/edit/${r.id}?tab=insights`} className="flex justify-between items-center text-sm hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors cursor-pointer group">
                <span className="truncate flex-1">
                  <span className="text-gray-400 mr-2">{i + 1}.</span>
                  <span className="group-hover:text-blue-600">{r.name}</span>
                </span>
                <span className="font-medium text-blue-600">{r.clicks}</span>
              </Link>
            ))}
            {(!insights.top_restaurants.by_websites || insights.top_restaurants.by_websites.length === 0) && (
              <p className="text-gray-400 text-sm">Noch keine Daten</p>
            )}
          </div>
        </div>
      </div>

      {/* Event Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="font-semibold mb-4">Event-Verteilung</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {insights.event_breakdown.map((e) => (
            <div key={e.event_name} className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 truncate">{e.event_name}</p>
              <p className="text-lg font-semibold">{e.count.toLocaleString('de-DE')}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const AdminDashboardPage = () => {
  const [_, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>("restaurants");
  const [sortOption, setSortOption] = useState<string>("Alphabetisch (A-Z)");
  const [filter, setFilter] = useState<string>("");

  // Fetch all restaurants for admin
  const { data: restaurants = [], isLoading } = useQuery<Restaurant[]>({
    queryKey: ["/api/restaurants"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/restaurants");
      if (!res.ok) throw new Error("Fehler beim Laden der Restaurants");
      return await res.json();
    },
    enabled: !!user && isAdmin(user),
    staleTime: 30000, // 30 Sekunden Cache
  });

  // Delete restaurant mutation
  const deleteRestaurantMutation = useMutation({
    mutationFn: async (restaurantId: string) => {
      const res = await apiRequest("DELETE", `/api/restaurants/${restaurantId}`);
      if (!res.ok) throw new Error("Fehler beim Löschen des Restaurants");
      return restaurantId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/restaurants"] });
      toast({
        title: "Restaurant gelöscht",
        description: "Das Restaurant wurde erfolgreich gelöscht.",
        variant: "destructive"
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Berechne Dashboard-Statistiken
  const dashboardStats: DashboardStats = {
    totalRestaurants: restaurants.length,
    activeRestaurants: restaurants.filter(r => r.enabled).length,
    deliveryRestaurants: restaurants.filter(r => r.delivery).length,
    averageRating: restaurants.length > 0 
      ? restaurants.reduce((sum, r) => sum + (r.rating || 0), 0) / restaurants.length
      : 0
  };

  // Ask for confirmation before deleting
  const handleDeleteRestaurant = (restaurantId: string, name: string) => {
    if (window.confirm(`Möchten Sie das Restaurant "${name}" wirklich löschen?`)) {
      deleteRestaurantMutation.mutate(restaurantId);
    }
  };

  // Filter restaurants by name
  const filteredRestaurants = filter
    ? restaurants.filter(restaurant => 
        restaurant.name.toLowerCase().includes(filter.toLowerCase()) ||
        restaurant.city.toLowerCase().includes(filter.toLowerCase()) ||
        restaurant.postalCode.includes(filter)
      )
    : restaurants;

  // Sort restaurants
  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (sortOption) {
      case "Neueste zuerst":
        return (b.updatedAt || '').localeCompare(a.updatedAt || '');
      case "Älteste zuerst":
        return (a.updatedAt || '').localeCompare(b.updatedAt || '');
      case "Alphabetisch (A-Z)":
        return a.name.localeCompare(b.name);
      case "Alphabetisch (Z-A)":
        return b.name.localeCompare(a.name);
      case "Ort (A-Z)":
        return a.city.localeCompare(b.city);
      default:
        return a.name.localeCompare(b.name);
    }
  });

  // Redirect if not admin
  useEffect(() => {
    if (user && !isAdmin(user)) {
      navigate("/");
      toast({
        title: "Zugriff verweigert",
        description: "Sie haben keine Berechtigung für diesen Bereich.",
        variant: "destructive"
      });
    }
  }, [user, navigate]);

  if (!user || !isAdmin(user)) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <h2 className="mt-4 text-xl font-medium text-gray-900">Überprüfe Berechtigungen...</h2>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ reset }) => (
        <div className="bg-gray-50 min-h-screen">
          <div className="bg-neutral-darker py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">Restaurant-Verwaltung</h1>
                  <p className="text-gray-300 mt-1">Verwalten Sie hier alle Restaurants</p>
                </div>
                <div className="mt-4 md:mt-0">
                  <CacheClearButton />
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            <AdminInlineErrorCard
              title="Admin Dashboard: UI-Fehler"
              description="Ein Bereich im Admin Dashboard ist abgestürzt. Die Seite läuft weiter – bitte lade diesen Bereich neu."
              onRetry={reset}
            />
          </div>
        </div>
      )}
    >
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-neutral-darker py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">Restaurant-Verwaltung</h1>
                <p className="text-gray-300 mt-1">Verwalten Sie hier alle Restaurants</p>
              </div>
              <div className="mt-4 md:mt-0">
                <CacheClearButton />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden">
            {/* Horizontal scrollbare Tab-Navigation, damit alle Punkte (inkl. API-Doku) sichtbar sind.
                overflow-y-hidden verhindert vertikales Scrollen innerhalb der Leiste auf Mobilgeräten. */}
            <nav className="flex -mb-px min-w-max px-2 sm:px-4 whitespace-nowrap">
              <button
                onClick={() => setActiveTab("restaurants")}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "restaurants"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Activity className="h-4 w-4 inline mr-2" />
                Restaurants
              </button>
              <button
                onClick={() => setActiveTab("status")}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "status"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Server className="h-4 w-4 inline mr-2" />
                System-Status
              </button>
              <button
                onClick={() => setActiveTab("metrics")}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "metrics"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <BarChart3 className="h-4 w-4 inline mr-2" />
                API-Metriken
              </button>
              <button
                onClick={() => setActiveTab("insights")}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "insights"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <BarChart3 className="h-4 w-4 inline mr-2" />
                Insights
              </button>

              <button
                onClick={() => setActiveTab("logs")}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === "logs"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Logs
              </button>
              <a
                href="/api-docs"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-primary hover:border-primary transition-colors duration-200 flex items-center ml-auto"
              >
                <BookOpen className="h-4 w-4 inline mr-2" />
                API-Dokumentation
                <ExternalLink className="h-3 w-3 inline ml-1.5" />
              </a>
            </nav>
          </div>
        </div>

        {/* Tab-Inhalt */}
        {activeTab === "restaurants" && (
          <ErrorBoundary
            fallbackRender={({ reset }) => (
              <AdminInlineErrorCard
                title="Restaurant-Liste konnte nicht gerendert werden"
                description="Ein UI-Fehler ist aufgetreten. Bitte lade den Bereich neu. Falls es wiederholt passiert: Cache leeren und erneut versuchen."
                onRetry={reset}
              />
            )}
          >
            <>
            {/* Dashboard-Statistiken */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary/10 text-primary">
                <Activity className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Restaurants</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardStats.totalRestaurants}</p>
                <p className="text-xs text-gray-500 mt-1">Insgesamt</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-50 text-green-600">
                <Users className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Aktive Restaurants</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardStats.activeRestaurants}</p>
                <p className="text-xs text-gray-500 mt-1">Online verfügbar</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-50 text-blue-600">
                <ShoppingBag className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Mit Lieferservice</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardStats.deliveryRestaurants}</p>
                <p className="text-xs text-gray-500 mt-1">Lieferoption aktiv</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-50 text-yellow-600">
                <Star className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Durchschn. Bewertung</p>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardStats.averageRating > 0 ? dashboardStats.averageRating.toFixed(1) : '—'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {dashboardStats.averageRating > 0 ? 'Kundenbewertung' : 'Noch keine Bewertungen'}
                </p>
                {dashboardStats.averageRating === 0 && (
                  <div className="mt-2">
                    <span className="inline-flex items-center text-xs text-gray-400">
                      <Star className="h-3 w-3 mr-1" />
                      Bewertungen kommen bald
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Restaurant-Liste */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-neutral-darker">Restaurant-Liste</h2>
              <p className="text-gray-500">Gesamt: {filteredRestaurants.length} Restaurants</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Nach Restaurant suchen..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md p-2 w-full sm:w-64 focus:ring-primary focus:border-primary"
              />

              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="border border-gray-300 rounded-md p-2 w-full sm:w-auto focus:ring-primary focus:border-primary"
              >
                <option>Neueste zuerst</option>
                <option>Älteste zuerst</option>
                <option>Alphabetisch (A-Z)</option>
                <option>Alphabetisch (Z-A)</option>
              </select>
            </div>
          </div>

          <Link href="/restaurants/create">
            <Button className="mb-6">
              <i className="fas fa-plus mr-2"></i>
              Neues Restaurant hinzufügen
            </Button>
          </Link>

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-2 text-gray-500">Lade Restaurants...</p>
            </div>
          ) : sortedRestaurants.length === 0 ? (
            <div className="text-center py-12 border rounded-lg">
              <i className="fas fa-utensils text-gray-400 text-4xl mb-3"></i>
              <h3 className="text-lg font-medium text-gray-900">Keine Restaurants gefunden</h3>
              <p className="text-gray-500">Fügen Sie ein neues Restaurant hinzu oder ändern Sie Ihre Filterkriterien.</p>
            </div>
          ) : (
            <>
              {/* Mobile: Karten-Ansicht */}
              <div className="space-y-4 md:hidden">
                {sortedRestaurants.map((restaurant) => (
                  <div
                    key={restaurant.id}
                    className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 uppercase text-sm">
                          {restaurant.name.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                            {restaurant.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {restaurant.postalCode} {restaurant.city}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-0.5 inline-flex text-[11px] leading-4 font-semibold rounded-full ${
                            restaurant.enabled
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {restaurant.enabled ? "Aktiv" : "Inaktiv"}
                        </span>
                        <span
                          className={`px-2 py-0.5 inline-flex text-[11px] leading-4 font-semibold rounded-full ${
                            restaurant.delivery
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {restaurant.delivery ? "Lieferung" : "Keine Lieferung"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
                      <Link href={`/restaurants/${restaurant.slug}`}>
                        <button className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 rounded-md text-sm bg-gray-100 text-gray-800 hover:bg-gray-200">
                          <i className="fas fa-eye mr-1.5" />
                          Ansehen
                        </button>
                      </Link>
                      <Link href={`/admin/restaurants/edit/${restaurant.id}`}>
                        <button className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 rounded-md text-sm bg-primary text-white hover:bg-primary-dark">
                          <i className="fas fa-edit mr-1.5" />
                          Bearbeiten
                        </button>
                      </Link>
                      <Link href={`/admin/restaurants/edit/${restaurant.id}?tab=insights`}>
                        <button className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 rounded-md text-sm bg-blue-50 text-blue-600 hover:bg-blue-100">
                          <BarChart3 className="h-4 w-4 mr-1.5" />
                          Insights
                        </button>
                      </Link>
                      <button
                        onClick={() => handleDeleteRestaurant(restaurant.id, restaurant.name)}
                        disabled={deleteRestaurantMutation.isPending}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 rounded-md text-sm bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60"
                      >
                        <i className="fas fa-trash mr-1.5" />
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop/Tablet: Tabellen-Ansicht */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Restaurant
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Ort
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Lieferung
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedRestaurants.map((restaurant) => (
                      <tr key={restaurant.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 uppercase">
                              {restaurant.name.charAt(0)}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 line-clamp-1">
                                {restaurant.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{restaurant.city}</div>
                          <div className="text-sm text-gray-500">{restaurant.postalCode}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            restaurant.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {restaurant.enabled ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            restaurant.delivery ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {restaurant.delivery ? 'Verfügbar' : 'Nicht verfügbar'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Link href={`/restaurants/${restaurant.slug}`}>
                              <button className="text-primary hover:text-primary-dark p-1 rounded transition-colors" title="Ansehen">
                                <Eye className="h-4 w-4" />
                              </button>
                            </Link>
                            <Link href={`/admin/restaurants/edit/${restaurant.id}`}>
                              <button className="text-green-600 hover:text-green-700 p-1 rounded transition-colors" title="Bearbeiten">
                                <Edit className="h-4 w-4" />
                              </button>
                            </Link>
                            <Link href={`/admin/restaurants/edit/${restaurant.id}?tab=insights`}>
                              <button className="text-blue-600 hover:text-blue-700 p-1 rounded transition-colors" title="Insights">
                                <BarChart3 className="h-4 w-4" />
                              </button>
                            </Link>
                            <button
                              onClick={() => handleDeleteRestaurant(restaurant.id, restaurant.name)}
                              disabled={deleteRestaurantMutation.isPending}
                              className="text-red-600 hover:text-red-700 p-1 rounded transition-colors disabled:opacity-50"
                              title="Löschen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
            </>
          </ErrorBoundary>
        )}

        {activeTab === "insights" && (
          <ErrorBoundary
            fallbackRender={({ reset }) => (
              <AdminInlineErrorCard
                title="Insights konnten nicht geladen werden"
                description="Ein UI-Fehler ist aufgetreten. Bitte diesen Bereich neu laden."
                onRetry={reset}
              />
            )}
          >
            <GlobalInsightsPanel />
          </ErrorBoundary>
        )}

        {activeTab === "status" && (
          <ErrorBoundary
            fallbackRender={({ reset }) => (
              <AdminInlineErrorCard
                title="System-Status konnte nicht geladen werden"
                description="Bitte erneut versuchen. Falls es wiederholt auftritt, prüfe DB/Docker-Status."
                onRetry={reset}
              />
            )}
          >
            <SystemStatus />
          </ErrorBoundary>
        )}
        {activeTab === "metrics" && (
          <ErrorBoundary
            fallbackRender={({ reset }) => (
              <AdminInlineErrorCard
                title="API-Metriken konnten nicht geladen werden"
                description="Bitte erneut versuchen. Bei hoher Last kann das kurzzeitig fehlschlagen."
                onRetry={reset}
              />
            )}
          >
            <ApiMetrics />
          </ErrorBoundary>
        )}
        {activeTab === "logs" && (
          <ErrorBoundary
            fallbackRender={({ reset }) => (
              <AdminInlineErrorCard
                title="Logs konnten nicht geladen werden"
                description="Bitte erneut versuchen. Wenn der Server gerade deployt, kann es kurz haken."
                onRetry={reset}
              />
            )}
          >
            <LogsViewer />
          </ErrorBoundary>
        )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AdminDashboardPage;